import { debounce } from '@mui/material';

import { Field, FieldType, flattenEmbeddedReducer } from '@/types/fields';

export const changeObserver = <T extends HTMLFormElement | HTMLFieldSetElement>(
  form: T,
  fields: readonly Field<any>[],
  callback: (form: T) => void,
): VoidFunction => {
  const elements = form.elements;

  const debouncedCallback = debounce(callback, 50);

  const changeHandler = (): void => {
    debouncedCallback(form);
  };

  const mutateHandler = (mutations: readonly MutationRecord[]): void => {
    const filteredMutations = mutations.filter(isMutationOfInterest);
    if (filteredMutations.length === 0) {
      return;
    }
    debouncedCallback(form);
  };

  const mutationObserver = new MutationObserver(mutateHandler);

  const cleanUpCallbacks = fields
    .reduce(flattenEmbeddedReducer, [])
    .reduce(
      (callbacks: readonly VoidFunction[], field: Field<unknown>): readonly VoidFunction[] => {
        const element = elements.namedItem(field.name);
        if (element === null) {
          if (field.type !== FieldType.multipleValues) {
            console.debug(`element is null ${field.name}`);
          }
          return callbacks;
        }

        if (element instanceof RadioNodeList) {
          if (field.type === FieldType.multipleValues) {
            return [
              ...callbacks,
              ...Array.from(elements).map((element: Element): VoidFunction => {
                element.addEventListener('change', changeHandler);
                return (): void => {
                  element.removeEventListener('change', changeHandler);
                };
              }),
            ];
          } else {
            const parent = getRadioNodeListParent(element);
            if (parent === null) {
              console.debug('cannot determine parent of radio node list');
              return callbacks;
            }

            for (const radio of element) {
              mutationObserver.observe(radio, {
                attributes: true,
                attributeOldValue: true,
                attributeFilter: ['value'],
              });
            }

            mutationObserver.observe(parent, {
              childList: true,
              subtree: true,
            });

            return callbacks;
          }
        }

        if (!isUserInputElement(element)) {
          console.debug(element);
          console.debug(`field "${field.name}" is not a user input element`);

          return callbacks;
        }

        if (isHiddenInput(element)) {
          mutationObserver.observe(element, {
            attributes: true,
            attributeOldValue: true,
            attributeFilter: ['value'],
          });
        }

        if (!isHiddenInput(element)) {
          element.addEventListener('blur', changeHandler);

          return [
            ...callbacks,
            (): void => {
              element.removeEventListener('blur', changeHandler);
            },
          ];
        } else {
          return callbacks;
        }
      },
      [],
    );

  return (): void => {
    cleanUpCallbacks.forEach((cleanUp: VoidFunction): void => {
      cleanUp();
    });

    mutationObserver.disconnect();
  };
};

const isHiddenInput = (element: Element): boolean => {
  return (
    (element instanceof HTMLInputElement && element.type === 'hidden') ||
    element.getAttribute('aria-hidden') === 'true'
  );
};

const isUserInputElement = (
  element: Element | null,
): element is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
  (element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement) &&
  element.hasAttribute('name');

const isHtmlElement = (node: Node): node is Element => node instanceof Element;

const isMutationOfInterest = (mutation: MutationRecord): boolean => {
  const { target } = mutation;

  if (!isHtmlElement(target)) {
    return false;
  }

  if (isUserInputElement(target)) {
    return mutation.oldValue !== target.value;
  }

  return false;
};

const getRadioNodeListParent = (element: RadioNodeList): Element | null => {
  if (element.length === 0) {
    return null;
  }

  return element[0].parentElement;
};
